import { useState } from 'react';
import { format, isToday as isTodayFn, isYesterday } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatDateToISO } from '@/lib/date-utils';
import { useLogDate } from '@/contexts/LogDateContext';

const label = (d: Date) => {
  if (isTodayFn(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'EEE, d MMM');
};

const LogDateBar = () => {
  const { date, dateISO, isToday, setDateISO, resetToToday } = useLogDate();
  const [open, setOpen] = useState(false);

  const shift = (days: number) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    if (next > new Date()) return;
    setDateISO(formatDateToISO(next));
  };

  return (
    <div className="mb-3 flex items-center justify-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-muted-foreground"
        onClick={() => shift(-1)}
        aria-label="Previous day"
      >
        <ChevronLeft size={16} />
      </Button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={isToday ? 'outline' : 'default'}
            size="sm"
            className="h-8 gap-2 px-3 text-sm font-medium"
            aria-label={`Logging to ${label(date)}. Tap to change date`}
          >
            <CalendarDays size={15} />
            {label(date)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              if (d) setDateISO(formatDateToISO(d));
              setOpen(false);
            }}
            disabled={(d) => d > new Date()}
            initialFocus
            className={cn('p-3 pointer-events-auto')}
          />
          {!isToday && (
            <div className="border-t p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  resetToToday();
                  setOpen(false);
                }}
              >
                Jump to today
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-muted-foreground"
        onClick={() => shift(1)}
        disabled={dateISO === formatDateToISO(new Date())}
        aria-label="Next day"
      >
        <ChevronRight size={16} />
      </Button>
    </div>
  );
};

export default LogDateBar;
